import * as React from 'react';
import { type San } from '@chess/api-model';
import type { MoveGrade } from '@chess/api-review';
import { tooltipOf, type Judgements, type SheetGrade } from './judgements';
import { sheetRows, type SheetCell } from './rows.ts';
import { HIDDEN_NOTE, isAsked, puzzleRows } from './puzzle-rows.ts';

// Two marks only: the engine's own move, and a blunder (under the user's
// rules, as the Blunders screen lists it). The grades between are left
// unmarked so the sheet stays quiet; the board's arrows carry them.
const Mark: React.FC<{ judgement?: MoveGrade }> = ({ judgement }) => {
  if (judgement === 'best') {
    return (
      <svg className="scoresheet-mark scoresheet-mark-best" viewBox="0 0 256 256" aria-hidden="true">
        <path d="M128 24a104 104 0 1 0 104 104A104.11 104.11 0 0 0 128 24Zm45.66 85.66-56 56a8 8 0 0 1-11.32 0l-24-24a8 8 0 0 1 11.32-11.32L112 148.69l50.34-50.35a8 8 0 0 1 11.32 11.32Z" />
      </svg>
    );
  }
  if (judgement === 'blunder') {
    return (
      <svg className="scoresheet-mark scoresheet-mark-blunder" viewBox="0 0 256 256" aria-hidden="true">
        <path d="M128 24a104 104 0 1 0 104 104A104.11 104.11 0 0 0 128 24Zm37.66 130.34a8 8 0 0 1-11.32 11.32L128 139.31l-26.34 26.35a8 8 0 0 1-11.32-11.32L116.69 128l-26.35-26.34a8 8 0 0 1 11.32-11.32L128 116.69l26.34-26.35a8 8 0 0 1 11.32 11.32L139.31 128Z" />
      </svg>
    );
  }
  return null;
};

interface MoveProps {
  san?: San;
  /** The ply reached after this move, 1-based. */
  ply: number;
  grade?: SheetGrade;
  branch?: boolean;
  current?: boolean;
  /** Called with the move's ply when it is chosen (the container goes to the position before it); absent when the sheet is not interactive. */
  onSelect?: (ply: number) => void;
}

// A move is a button: choosing it puts the position after it on the board.
const Move: React.FC<MoveProps> = ({ san, ply, grade, branch, current, onSelect }) => {
  if (!san) return <td className="scoresheet-move" />;
  const judgement = grade?.grade;
  // The tooltip carries the score change the scan recorded for the move.
  const title = [grade && tooltipOf(grade), branch && 'Your line starts after this move'].filter(Boolean).join(' · ') || undefined;
  const classes = ['scoresheet-move'];
  if (judgement) classes.push(`scoresheet-${judgement}`);
  if (branch) classes.push('scoresheet-branch');
  if (current) classes.push('scoresheet-current');
  return (
    <td className={classes.join(' ')} title={title}>
      <button
        type="button"
        className="scoresheet-pick"
        disabled={!onSelect}
        aria-current={current ? 'true' : undefined}
        onClick={onSelect ? () => onSelect(ply) : undefined}>
        <span className="scoresheet-san">{san}</span>
        <Mark judgement={judgement} />
      </button>
    </td>
  );
};

export interface ScoresheetProps {
  moves: readonly San[];
  judgements?: Judgements;
  /** The game ply the reader's own line starts from; the move that led there is marked. */
  branchPly?: number;
  /** While a puzzle is being solved: moves from this ply on are its answer and are hidden. */
  concealFrom?: number;
  /** The ply on the board; the move that led to it is highlighted. */
  currentPly?: number;
  /** Choosing a move: called with its ply; the container goes to the position before it (2026-10-13). Absent when choosing is not allowed. */
  onSelect?: (ply: number) => void;
}

/**
 * The scoresheet: the moves, each marked when it was the engine's move or a
 * blunder, the one on the board highlighted, and each a button that puts the
 * position after it on the board. While a puzzle conceals the moves from
 * its position on, the sheet ends at the puzzle's move, drawn as a "?" in
 * the accent, and one muted line says the rest is hidden (puzzle-rows.ts).
 */
export const Scoresheet: React.FC<ScoresheetProps> = ({ moves, judgements, branchPly, concealFrom, currentPly, onSelect }) => {
  if (moves.length === 0) {
    return <p className="panel-empty">No moves.</p>;
  }
  const rows = puzzleRows(sheetRows(moves, { judgements, branchPly, concealFrom, currentPly }), concealFrom);
  const capped = concealFrom !== undefined && concealFrom < moves.length;
  const cell = (c: SheetCell) =>
    c.concealed ? (
      isAsked(c.index, concealFrom) ? (
        <td className="scoresheet-asked" title="The puzzle: find the better move here">
          <span className="scoresheet-ask">?</span>
        </td>
      ) : (
        <td className="scoresheet-concealed" />
      )
    ) : (
      <Move san={c.san} ply={c.ply} grade={c.grade} branch={c.branch} current={c.current} onSelect={onSelect} />
    );
  return (
    <>
      <table className="scoresheet">
        <tbody>
          {rows.map((row) => (
            <tr key={row.number}>
              <td className="scoresheet-number">{row.number}.</td>
              {cell(row.white)}
              {cell(row.black)}
            </tr>
          ))}
        </tbody>
      </table>
      {capped && <p className="scoresheet-hidden-note">{HIDDEN_NOTE}</p>}
    </>
  );
};
